import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

const TOKEN_KEY = "vbg_admin_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Separate instance + token for candidate portal
const CAND_KEY = "vbg_candidate_token";
export const getCandToken = () => localStorage.getItem(CAND_KEY);
export const setCandToken = (t) => (t ? localStorage.setItem(CAND_KEY, t) : localStorage.removeItem(CAND_KEY));
export const candApi = axios.create({ baseURL: API });
candApi.interceptors.request.use((cfg) => {
  const t = getCandToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// Password policy: 10+ chars, 1 uppercase, 1 number, 1 symbol. Returns error string or "".
export function validatePassword(pw) {
  if (pw.length < 10) return "Password must be at least 10 characters.";
  if (!/[A-Z]/.test(pw)) return "Include at least one uppercase letter.";
  if (!/\d/.test(pw)) return "Include at least one number.";
  if (!/[^A-Za-z0-9]/.test(pw)) return "Include at least one symbol.";
  return "";
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Public
export const submitContact = (data) => api.post("/contact", data).then((r) => r.data);
export const submitPortalAccess = (data) => api.post("/portal-access", data).then((r) => r.data);
export const getJobs = () => api.get("/jobs").then((r) => r.data);
export const submitApplication = (formData) =>
  api.post("/applications", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);

// Auth (shared login endpoint)
export const login = (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data);
export const getMe = () => api.get("/auth/me").then((r) => r.data);
export const listAdmins = () => api.get("/auth/admins").then((r) => r.data);
export const createAdmin = (data) => api.post("/auth/admins", data).then((r) => r.data);

// Password reset (public)
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, new_password) => api.post("/auth/reset-password", { token, new_password }).then((r) => r.data);

// Admin jobs
export const adminJobs = () => api.get("/admin/jobs").then((r) => r.data);
export const createJob = (data) => api.post("/admin/jobs", data).then((r) => r.data);
export const updateJob = (id, data) => api.put(`/admin/jobs/${id}`, data).then((r) => r.data);
export const deleteJob = (id) => api.delete(`/admin/jobs/${id}`).then((r) => r.data);

// Admin applicants
export const adminApplications = () => api.get("/admin/applications").then((r) => r.data);
export const updateApplicationStatus = (id, status) => api.put(`/admin/applications/${id}/status`, { status }).then((r) => r.data);
export async function exportApplications() {
  const res = await api.get("/admin/applications/export", { responseType: "blob" });
  triggerDownload(res.data, "vectorbridge-applicants.csv");
}
export async function downloadResume(appId, filename) {
  const res = await api.get(`/admin/applications/${appId}/resume`, { responseType: "blob" });
  triggerDownload(res.data, filename || "resume");
}

// Talent pool
export const adminTalent = (params = {}) => api.get("/admin/talent", { params }).then((r) => r.data);
export const createTalent = (data) => api.post("/admin/talent", data).then((r) => r.data);
export const updateTalent = (id, data) => api.put(`/admin/talent/${id}`, data).then((r) => r.data);
export const deleteTalent = (id) => api.delete(`/admin/talent/${id}`).then((r) => r.data);
export const promoteApplicant = (appId, data) => api.post(`/admin/applications/${appId}/promote`, data).then((r) => r.data);
export const runDeadlineCheck = () => api.post("/admin/jobs/run-deadline-check").then((r) => r.data);
export async function downloadTalentResume(id, filename) {
  const res = await api.get(`/admin/talent/${id}/resume`, { responseType: "blob" });
  triggerDownload(res.data, filename || "resume");
}

// Admin candidates + HR forms
export const adminCandidates = () => api.get("/admin/candidates").then((r) => r.data);
export const createCandidate = (data) => api.post("/admin/candidates", data).then((r) => r.data);
export const adminHrForms = (candidateId) => api.get("/admin/hr-forms", { params: candidateId ? { candidateId } : {} }).then((r) => r.data);
export const createHrForm = (formData) => api.post("/admin/hr-forms", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const deleteHrForm = (id) => api.delete(`/admin/hr-forms/${id}`).then((r) => r.data);
export async function adminDownloadHrForm(id, which, filename) {
  const res = await api.get(`/admin/hr-forms/${id}/${which}`, { responseType: "blob" });
  triggerDownload(res.data, filename || which);
}

// Candidate portal
export const candidateLogin = async (email, password) => {
  const data = await login(email, password);
  setCandToken(data.token);
  return data;
};
export const candidateMe = () => candApi.get("/auth/me").then((r) => r.data);
export const candidateChangePassword = (new_password) => candApi.post("/auth/change-password", { new_password }).then((r) => r.data);
export const candidateForms = () => candApi.get("/candidate/hr-forms").then((r) => r.data);
export const candidateSubmitForm = (id, formData) =>
  candApi.post(`/candidate/hr-forms/${id}/complete`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export async function candidateDownload(id, which, filename) {
  const res = await candApi.get(`/candidate/hr-forms/${id}/${which}`, { responseType: "blob" });
  triggerDownload(res.data, filename || which);
}

// Client portal (separate token)
const CLIENT_KEY = "vbg_client_token";
export const getClientToken = () => localStorage.getItem(CLIENT_KEY);
export const setClientToken = (t) => (t ? localStorage.setItem(CLIENT_KEY, t) : localStorage.removeItem(CLIENT_KEY));
export const clientApi = axios.create({ baseURL: API });
clientApi.interceptors.request.use((cfg) => {
  const t = getClientToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
export const clientLogin = async (email, password) => {
  const d = await login(email, password);
  setClientToken(d.token);
  return d;
};
export const clientMe = () => clientApi.get("/auth/me").then((r) => r.data);
export const clientChangePassword = (new_password) => clientApi.post("/auth/change-password", { new_password }).then((r) => r.data);
export const clientRequisitions = () => clientApi.get("/client/requisitions").then((r) => r.data);
export const clientDocuments = () => clientApi.get("/client/documents").then((r) => r.data);
export async function clientDownloadDoc(id, filename) {
  const res = await clientApi.get(`/client/documents/${id}`, { responseType: "blob" });
  triggerDownload(res.data, filename || "document");
}

// Admin: clients, requisitions, submittals, client docs
export const adminClients = () => api.get("/admin/clients").then((r) => r.data);
export const createClient = (d) => api.post("/admin/clients", d).then((r) => r.data);
export const adminRequisitions = (clientId) => api.get("/admin/requisitions", { params: clientId ? { clientId } : {} }).then((r) => r.data);
export const createRequisition = (d) => api.post("/admin/requisitions", d).then((r) => r.data);
export const updateRequisition = (id, d) => api.put(`/admin/requisitions/${id}`, d).then((r) => r.data);
export const deleteRequisition = (id) => api.delete(`/admin/requisitions/${id}`).then((r) => r.data);
export const addSubmittal = (rid, d) => api.post(`/admin/requisitions/${rid}/submittals`, d).then((r) => r.data);
export const updateSubmittal = (rid, sid, d) => api.put(`/admin/requisitions/${rid}/submittals/${sid}`, d).then((r) => r.data);
export const deleteSubmittal = (rid, sid) => api.delete(`/admin/requisitions/${rid}/submittals/${sid}`).then((r) => r.data);
export const adminClientDocs = (clientId) => api.get("/admin/client-docs", { params: clientId ? { clientId } : {} }).then((r) => r.data);
export const uploadClientDoc = (formData) => api.post("/admin/client-docs", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const deleteClientDoc = (id) => api.delete(`/admin/client-docs/${id}`).then((r) => r.data);
export async function adminDownloadClientDoc(id, filename) {
  const res = await api.get(`/admin/client-docs/${id}`, { responseType: "blob" });
  triggerDownload(res.data, filename || "document");
}
